import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTypewriter, useChatStream } from '@airia/ui';
export function useAddinStream(options) {
    const { agentId, bearerToken, apiUrl, conversationId, variables, onError, onFinish } = options;
    // Typewriter for smooth text reveal - matches /omni behavior
    const typewriter = useTypewriter({
        enabled: true,
        speed: 15,
        chunkSize: 3,
        chunkThreshold: 50,
    });
    const typewriterRef = useRef(typewriter);
    typewriterRef.current = typewriter;
    const chatStream = useChatStream({
        agentId,
        bearerToken,
        apiUrl,
        conversationId,
        onError,
        onFinish: () => {
            // Flush typewriter buffer atomically before signaling finish
            typewriterRef.current.forceSync();
            onFinish?.();
        },
        onEvent: {
            onStreamFragment: (msg) => {
                if (msg.Index !== undefined && msg.Index >= 0) {
                    typewriter.appendToBuffer(msg.Content ?? '');
                }
            },
            onOutputMessage: (msg) => {
                typewriter.appendToBuffer(msg.StepResult ?? '');
            },
        },
    });
    // Track previous conversationId for reset
    const prevConversationIdRef = useRef(null);
    useEffect(() => {
        if (prevConversationIdRef.current !== null &&
            prevConversationIdRef.current !== conversationId) {
            chatStream.clear();
            typewriter.reset();
        }
        prevConversationIdRef.current = conversationId;
    }, [conversationId, typewriter, chatStream.clear]);
    // Map StreamMessage[] → AddinMessage[] for useChatMessages compatibility
    const messages = useMemo(() => chatStream.messages
        .filter((m) => m.message != null && m.message !== '')
        .map((m) => ({
        id: m.id,
        value: m.message,
        role: m.role,
        timestamp: m.createdAt ? new Date(m.createdAt) : undefined,
    })), [chatStream.messages]);
    // Derive streamingMessageId from the reducer's messages
    const streamingMessageId = useMemo(() => {
        if (!chatStream.isStreaming)
            return null;
        const executionId = chatStream.state.executionId;
        if (!executionId)
            return null;
        // Find the assistant message matching the current execution
        const streamingMsg = chatStream.messages.find((m) => m.role === 'assistant' && m.executionId === executionId);
        return streamingMsg?.id ?? null;
    }, [chatStream.isStreaming, chatStream.state.executionId, chatStream.messages]);
    // Build inMemoryMessages from the reducer's message list for context
    const messagesRef = useRef(chatStream.messages);
    messagesRef.current = chatStream.messages;
    const sendMessage = useCallback(async (userInput, documentContext, userId, fileUrls) => {
        typewriter.reset();
        // Build the full input with document context prepended if available
        const fullUserInput = documentContext
            ? `<document_context>\n${documentContext}\n</document_context>\n\n${userInput}`
            : userInput;
        await chatStream.submit({
            data: {
                userInput: fullUserInput,
                userId,
                images: fileUrls?.images,
                files: fileUrls?.files,
                inMemoryMessages: messagesRef.current.map((m) => ({
                    role: m.role,
                    message: m.message,
                })),
                variables,
            },
        });
    }, [chatStream.submit, variables, typewriter]);
    const clearMessages = useCallback(() => {
        chatStream.clear();
        typewriter.reset();
    }, [chatStream.clear, typewriter]);
    return {
        messages,
        events: chatStream.rawEvents,
        submit: sendMessage,
        isLoading: chatStream.isLoading,
        isStreaming: chatStream.isStreaming,
        isCompacting: chatStream.state.isCompacting,
        streamingMessageId,
        error: chatStream.error
            ? typeof chatStream.error === 'string'
                ? chatStream.error
                : (chatStream.error.message ?? 'Stream error')
            : null,
        clear: clearMessages,
        typewriterState: {
            displayedText: typewriter.displayedText,
            bufferText: typewriter.bufferText,
            isTyping: typewriter.isTyping,
        },
    };
}
